import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import './Admin.css'

function AdminDashboard() {
  const navigate = useNavigate()
  const token = sessionStorage.getItem('adminToken')
  const isValid = useQuery(api.admin.verifySession, token ? { token } : 'skip')
  const logoutMutation = useMutation(api.admin.logout)

  useEffect(() => {
    if (!token || isValid === false) {
      sessionStorage.removeItem('adminToken')
      navigate('/admin')
    }
  }, [token, isValid, navigate])

  const handleLogout = async () => {
    if (token) await logoutMutation({ token })
    sessionStorage.removeItem('adminToken')
    navigate('/admin')
  }

  if (isValid === undefined) {
    return <div className="admin-loading"><p>Loading...</p></div>
  }
  if (!isValid) return null

  return (
    <div className="live-editor">
      {/* Floating admin toolbar */}
      <div className="editor-toolbar">
        <div className="editor-toolbar-left">
          <img src="/images/omnitax.png" alt="" className="editor-toolbar-logo" />
          <span className="editor-toolbar-label">Editing Mode</span>
        </div>
        <div className="editor-toolbar-right">
          <a href="/" target="_blank" rel="noopener noreferrer" className="editor-btn editor-btn-ghost">
            View Live Site
          </a>
          <button onClick={handleLogout} className="editor-btn editor-btn-ghost editor-btn-logout">
            Logout
          </button>
        </div>
      </div>

      {/* Live site preview with editable sections */}
      <div className="editor-canvas">
        <HeroEditor token={token} />
        <ServicesEditor token={token} />
        <CtaEditor token={token} />
        <ContactEditor token={token} />
        <TeamEditor token={token} />
      </div>
    </div>
  )
}

// ── Inline editable text ─────────────────────────────────────
function EditableText({ value, onChange, tag: Tag = 'span', className = '', multiline = false }) {
  const ref = useRef(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  useEffect(() => { setDraft(value) }, [value])

  const startEdit = () => {
    setEditing(true)
    setDraft(value)
    setTimeout(() => ref.current?.focus(), 0)
  }

  const save = () => {
    setEditing(false)
    if (draft !== value) onChange(draft)
  }

  const cancel = () => {
    setEditing(false)
    setDraft(value)
  }

  if (editing) {
    return (
      <div className="editable-wrapper">
        {multiline ? (
          <textarea
            ref={ref}
            className={`editable-input ${className}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancel()
            }}
            rows={3}
          />
        ) : (
          <input
            ref={ref}
            type="text"
            className={`editable-input ${className}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') cancel()
            }}
          />
        )}
        <div className="editable-actions">
          <button className="editable-save" onClick={save}>Save</button>
          <button className="editable-cancel" onClick={cancel}>Cancel</button>
        </div>
      </div>
    )
  }

  return (
    <Tag className={`editable-text ${className}`} onClick={startEdit} title="Click to edit">
      {value.split('\n').map((line, i) => (
        <span key={i}>{line}{i < value.split('\n').length - 1 && <br />}</span>
      ))}
      <span className="editable-icon">&#9998;</span>
    </Tag>
  )
}

// ── Hero Section Editor ──────────────────────────────────────
function HeroEditor({ token }) {
  const settings = useQuery(api.siteSettings.get)
  const upsert = useMutation(api.siteSettings.upsert)
  const [saving, setSaving] = useState(false)

  if (!settings) return <section className="hero"><div className="container">Loading...</div></section>

  const updateField = async (field, value) => {
    setSaving(true)
    try {
      await upsert({ token, ...settings, _id: undefined, _creationTime: undefined, [field]: value })
    } catch (e) { alert('Save failed: ' + e.message) }
    finally { setSaving(false) }
  }

  return (
    <section className="hero editor-section">
      <div className="editor-section-label">Hero Section</div>
      {saving && <div className="editor-saving">Saving...</div>}
      <div className="container hero-container">
        <div className="hero-content">
          <EditableText
            tag="h1"
            className="hero-title"
            value={settings.heroTitle}
            onChange={(v) => updateField('heroTitle', v)}
            multiline
          />
          <EditableText
            tag="p"
            className="hero-subtitle"
            value={settings.heroSubtitle}
            onChange={(v) => updateField('heroSubtitle', v)}
            multiline
          />
          <div className="btn-cta" style={{ cursor: 'default' }}>
            <EditableText
              value={settings.heroCta}
              onChange={(v) => updateField('heroCta', v)}
            />
            <span className="btn-arrow">&rarr;</span>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-image-wrapper">
            <img src="/images/about.jpg" alt="Professional accountant" />
            <div className="circle-accent circle-1"></div>
            <div className="circle-accent circle-2"></div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Services Editor ──────────────────────────────────────────
function ServicesEditor({ token }) {
  const settings = useQuery(api.siteSettings.get)
  const services = useQuery(api.services.list)
  const upsert = useMutation(api.siteSettings.upsert)
  const updateService = useMutation(api.services.update)
  const createService = useMutation(api.services.create)
  const removeService = useMutation(api.services.remove)
  const [adding, setAdding] = useState(false)
  const [newService, setNewService] = useState({ title: '', description: '', linkText: '' })

  if (!settings || !services) return null

  const updateSettingsField = async (field, value) => {
    try {
      await upsert({ token, ...settings, _id: undefined, _creationTime: undefined, [field]: value })
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  const updateServiceField = async (service, field, value) => {
    try {
      await updateService({
        token,
        id: service._id,
        title: service.title,
        description: service.description,
        linkText: service.linkText,
        order: service.order,
        isActive: service.isActive,
        [field]: value,
      })
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  const handleAddService = async () => {
    if (!newService.title) return
    try {
      await createService({
        token,
        title: newService.title,
        description: newService.description,
        linkText: newService.linkText || newService.title,
        order: (services.length || 0) + 1,
        isActive: true,
      })
      setNewService({ title: '', description: '', linkText: '' })
      setAdding(false)
    } catch (e) { alert('Failed: ' + e.message) }
  }

  const handleToggle = async (service) => {
    await updateServiceField(service, 'isActive', !service.isActive)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this service?')) return
    try { await removeService({ token, id }) }
    catch (e) { alert('Failed: ' + e.message) }
  }

  return (
    <section className="services editor-section">
      <div className="editor-section-label">Services Section</div>
      <div className="container">
        <div className="services-header">
          <EditableText
            tag="h2"
            value={settings.servicesHeading}
            onChange={(v) => updateSettingsField('servicesHeading', v)}
            multiline
          />
          <EditableText
            tag="p"
            className="section-description"
            value={settings.servicesSubheading}
            onChange={(v) => updateSettingsField('servicesSubheading', v)}
            multiline
          />
        </div>
        <div className="services-grid">
          {services.map((service) => (
            <div key={service._id} className={`service-card editor-card ${!service.isActive ? 'editor-inactive' : ''}`}>
              <div className="editor-card-controls">
                <button
                  className={`editor-toggle ${service.isActive ? 'on' : 'off'}`}
                  onClick={() => handleToggle(service)}
                  title={service.isActive ? 'Active - click to hide' : 'Hidden - click to show'}
                >
                  {service.isActive ? 'Visible' : 'Hidden'}
                </button>
                <button className="editor-delete" onClick={() => handleDelete(service._id)} title="Delete">
                  &times;
                </button>
              </div>
              <div className="service-icon-wrapper">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <EditableText
                tag="h3"
                value={service.title}
                onChange={(v) => updateServiceField(service, 'title', v)}
              />
              <EditableText
                tag="p"
                value={service.description}
                onChange={(v) => updateServiceField(service, 'description', v)}
                multiline
              />
              <EditableText
                tag="span"
                className="service-link"
                value={service.linkText}
                onChange={(v) => updateServiceField(service, 'linkText', v)}
              />
            </div>
          ))}

          {/* Add service card */}
          {adding ? (
            <div className="service-card editor-add-card">
              <input
                type="text"
                placeholder="Service title"
                className="editor-inline-input"
                value={newService.title}
                onChange={(e) => setNewService(p => ({ ...p, title: e.target.value }))}
                autoFocus
              />
              <textarea
                placeholder="Description"
                className="editor-inline-input"
                rows="2"
                value={newService.description}
                onChange={(e) => setNewService(p => ({ ...p, description: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Link text"
                className="editor-inline-input"
                value={newService.linkText}
                onChange={(e) => setNewService(p => ({ ...p, linkText: e.target.value }))}
              />
              <div className="editor-add-actions">
                <button className="editable-save" onClick={handleAddService}>Add</button>
                <button className="editable-cancel" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="service-card editor-add-trigger" onClick={() => setAdding(true)}>
              <span className="editor-add-icon">+</span>
              <span>Add Service</span>
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

// ── CTA Editor ───────────────────────────────────────────────
function CtaEditor({ token }) {
  const settings = useQuery(api.siteSettings.get)
  const upsert = useMutation(api.siteSettings.upsert)

  if (!settings) return null

  const updateField = async (field, value) => {
    try {
      await upsert({ token, ...settings, _id: undefined, _creationTime: undefined, [field]: value })
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  return (
    <section className="cta editor-section">
      <div className="editor-section-label">Call to Action</div>
      <div className="container cta-container">
        <EditableText tag="h2" value={settings.ctaHeading} onChange={(v) => updateField('ctaHeading', v)} />
        <EditableText tag="p" value={settings.ctaSubheading} onChange={(v) => updateField('ctaSubheading', v)} multiline />
        <div className="btn-cta btn-cta-dark" style={{ cursor: 'default' }}>
          Book Your Consultation <span className="btn-arrow">&rarr;</span>
        </div>
      </div>
    </section>
  )
}

// ── Contact Info Editor ──────────────────────────────────────
function ContactEditor({ token }) {
  const settings = useQuery(api.siteSettings.get)
  const upsert = useMutation(api.siteSettings.upsert)

  if (!settings) return null

  const updateField = async (field, value) => {
    try {
      await upsert({ token, ...settings, _id: undefined, _creationTime: undefined, [field]: value })
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  return (
    <section className="editor-section editor-contact-section">
      <div className="editor-section-label">Contact Information</div>
      <div className="container">
        <div className="editor-contact-grid">
          <div className="editor-contact-item">
            <label>Email</label>
            <EditableText value={settings.contactEmail} onChange={(v) => updateField('contactEmail', v)} />
          </div>
          <div className="editor-contact-item">
            <label>Phone</label>
            <EditableText value={settings.contactPhone} onChange={(v) => updateField('contactPhone', v)} />
          </div>
          <div className="editor-contact-item">
            <label>Cell</label>
            <EditableText value={settings.contactCell} onChange={(v) => updateField('contactCell', v)} />
          </div>
          <div className="editor-contact-item">
            <label>Address</label>
            <EditableText value={settings.address} onChange={(v) => updateField('address', v)} multiline />
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Team Editor ──────────────────────────────────────────────
function TeamEditor({ token }) {
  const members = useQuery(api.teamMembers.list)
  const createMember = useMutation(api.teamMembers.create)
  const updateMember = useMutation(api.teamMembers.update)
  const removeMember = useMutation(api.teamMembers.remove)
  const [adding, setAdding] = useState(false)
  const [newMember, setNewMember] = useState({ name: '', role: '', bio: '' })

  if (!members) return null

  const updateField = async (member, field, value) => {
    try {
      await updateMember({
        token,
        id: member._id,
        name: member.name,
        role: member.role,
        bio: member.bio,
        order: member.order,
        isActive: member.isActive,
        [field]: value,
      })
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  const handleAdd = async () => {
    if (!newMember.name) return
    try {
      await createMember({
        token,
        name: newMember.name,
        role: newMember.role,
        bio: newMember.bio,
        order: (members.length || 0) + 1,
        isActive: true,
      })
      setNewMember({ name: '', role: '', bio: '' })
      setAdding(false)
    } catch (e) { alert('Failed: ' + e.message) }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this team member?')) return
    try { await removeMember({ token, id }) }
    catch (e) { alert('Failed: ' + e.message) }
  }

  const handleToggle = async (member) => {
    await updateField(member, 'isActive', !member.isActive)
  }

  return (
    <section className="editor-section editor-team-section">
      <div className="editor-section-label">Team Members</div>
      <div className="container">
        <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Our Team</h2>
        <div className="team-grid">
          {members.map((member) => (
            <div key={member._id} className={`team-card editor-card ${!member.isActive ? 'editor-inactive' : ''}`}>
              <div className="editor-card-controls">
                <button
                  className={`editor-toggle ${member.isActive ? 'on' : 'off'}`}
                  onClick={() => handleToggle(member)}
                >
                  {member.isActive ? 'Visible' : 'Hidden'}
                </button>
                <button className="editor-delete" onClick={() => handleDelete(member._id)}>&times;</button>
              </div>
              <EditableText tag="h3" value={member.name} onChange={(v) => updateField(member, 'name', v)} />
              <EditableText tag="p" className="role" value={member.role} onChange={(v) => updateField(member, 'role', v)} />
              <EditableText tag="p" value={member.bio} onChange={(v) => updateField(member, 'bio', v)} multiline />
            </div>
          ))}

          {adding ? (
            <div className="team-card editor-add-card">
              <input type="text" placeholder="Name" className="editor-inline-input" value={newMember.name}
                onChange={(e) => setNewMember(p => ({ ...p, name: e.target.value }))} autoFocus />
              <input type="text" placeholder="Role" className="editor-inline-input" value={newMember.role}
                onChange={(e) => setNewMember(p => ({ ...p, role: e.target.value }))} />
              <textarea placeholder="Bio" className="editor-inline-input" rows="3" value={newMember.bio}
                onChange={(e) => setNewMember(p => ({ ...p, bio: e.target.value }))} />
              <div className="editor-add-actions">
                <button className="editable-save" onClick={handleAdd}>Add</button>
                <button className="editable-cancel" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="team-card editor-add-trigger" onClick={() => setAdding(true)}>
              <span className="editor-add-icon">+</span>
              <span>Add Team Member</span>
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminDashboard
